import {useMutation} from '@apollo/client'
import {useEffect, useState, FormEvent} from 'react'
import {Button, Modal, Spinner, Form} from 'react-bootstrap'
import {toast} from 'react-toastify'
import {UPDATE_COMMISSION_REPORT} from '../../../gql/mutations/commissionReportMutations'
import {IResUpdateCommissionReport, NodeCommissionReport} from '../../../types/commissionReportApi'
import {parseId} from '../../../helpers'

interface IModalEditProps {
  isOpen: boolean
  onClose: () => void
  infoEdit: NodeCommissionReport | null
  refetch: () => void
}

export const ModalEditCommissionReport: React.FC<IModalEditProps> = ({isOpen, onClose, infoEdit, refetch}) => {
  const [formData, setFormData] = useState({
    name: '',
    paidDate: '',
    approvePreviousYear: false,
  })

  const [gqlUpdate, {loading, data, error}] = useMutation<IResUpdateCommissionReport>(UPDATE_COMMISSION_REPORT)

  useEffect(() => {
    if (infoEdit) {
      setFormData({
        name: infoEdit.name || '',
        paidDate: infoEdit.paidDate?.slice(0, 10) || '', // formato YYYY-MM-DD
        approvePreviousYear: infoEdit.approvePreviousYear || false,
      })
    }
  }, [infoEdit])

  useEffect(() => {
    if (!data) return
    const res = data.commissionReportUpdateMutation
    if (res?.success) {
      toast.success('Commission Report updated successfully')
      refetch()
      onClose()
    } else if (res?.errors?.length) {
      toast.error(`Error: ${res.errors[0]?.message}`)
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data])

  useEffect(() => {
    if (error) toast.error(`Error: ${error.message}`)
  }, [error])

  const handleChange = (field: string, value: string | boolean) => {
    setFormData(prev => ({...prev, [field]: value}))
  }

  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    if (!infoEdit) return

    gqlUpdate({
      variables: {
        id: parseId(infoEdit.id),
        input: {
          name: formData.name,
          paidDate: formData.paidDate || null,
          approvePreviousYear: formData.approvePreviousYear,
        },
      },
    })
  }

  return (
    <Modal show={isOpen} onHide={onClose}>
      <Modal.Header closeButton>
        <Modal.Title>Edit Commission Report</Modal.Title>
      </Modal.Header>
      <form onSubmit={handleSubmit}>
        <Modal.Body>
          <Form.Group className='mb-3'>
            <Form.Label>Report title</Form.Label>
            <Form.Control
              type='text'
              value={formData.name}
              onChange={e => handleChange('name', e.target.value)}
              required
            />
          </Form.Group>
          <Form.Group className='mb-3'>
            <Form.Label>Paid on</Form.Label>
            <Form.Control
              type='date'
              value={formData.paidDate}
              onChange={e => handleChange('paidDate', e.target.value)}
            />
          </Form.Group>
          <Form.Group className='mb-3'>
            <Form.Check
              type='switch'
              id='approvePreviousYearSwitchEdit'
              label='Approve policies found in previous year'
              checked={formData.approvePreviousYear}
              onChange={e => handleChange('approvePreviousYear', e.target.checked)}
            />
            <div className='form-text'>
              If enabled, policies found in the previous year will be marked as APPROVED_DISCREPANCY instead of NOT_FOUND.
            </div>
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant='secondary' onClick={onClose}>CLOSE</Button>
          <Button type='submit' variant='primary' disabled={loading}>
            {loading && <Spinner animation='border' size='sm' className='me-2' />}
            SAVE
          </Button>
        </Modal.Footer>
      </form>
    </Modal>
  )
}
